<template>
  <base-layout>
    <h1 slot="header">
      {{ `app.pages.subscription` | trans }}
    </h1>
    <a slot="buttons" :href="routes.subscription" class="btn btn-primary">
      <i class="fa fa-arrow-circle-left" aria-hidden="true"></i>
      {{ `app.back` | trans }}
    </a>
    <v-alert type="success" :value="selected_subscription.role_to_assign === DIETITIAN_STUDENT()">
      {{ `app.student_status_verification_required` | trans }}
    </v-alert>
    <v-app id="vuetify-app" class="no--wrap box box-success">
      <v-container>
        <v-layout justify-center row wrap class="box-body">
          <v-flex xs12>
            <h3 class="subscription-summary__header">
              Wybrany pakiet: <strong>{{ selected_subscription.name }}</strong>
            </h3>
            <h4 class="subscription-summary__header header--dates">
              {{ getNewSubscriptionStartDate().format('DD.MM.Y') }} -
              {{ getNewSubscriptionEndDate().format('DD.MM.Y') }}
            </h4>
          </v-flex>
          <v-flex xs6 class="subscription-summary__address">
            <span class="address__header">SPRZEDAWCA</span>
            {{ seller.name }}
            {{ seller.address }}
            {{ seller.address2 }}
            {{ seller.nip }}
          </v-flex>
          <v-flex xs6 class="subscription-summary__address">
            <span class="address__header">NABYWCA</span>
            {{ displayedInvoice.name }}
            {{ displayedInvoice.street_address }}
            {{ displayedInvoice.postal_code }} {{ invoice.city }}
            {{ countryName(displayedInvoice.country) }}
            <span v-if="displayedInvoice.type === 'company'">NIP: {{ displayedInvoice.nip }}</span>
            <v-flex>
              <v-btn color="success" class="mr-0 ml-0" @click="editInvoice" small>
                {{ 'app.edit' | trans }}
              </v-btn>
            </v-flex>
          </v-flex>
          <v-flex xs12 class="mt-5">
            <table class="table">
              <thead>
                <tr>
                  <th>NAZWA USŁUGI</th>
                  <th width="150" class="text-right">BRUTTO</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    {{ selected_subscription.name }} - {{ selectedSubscriptionDuration }}
                    {{ polishPlurals('dzień', 'dni', 'dni', selectedSubscriptionDuration) }}
                  </td>
                  <td width="150" class="text-right">{{ getGrossPrice() | money }}</td>
                </tr>
                <tr
                  v-if="
                    isUpgradingSubscription() &&
                    current_subscription.unused_days > 0 &&
                    current_subscription.subscription_type.role_to_assign !== DIETITIAN_TEST()
                  "
                >
                  <td>
                    {{
                      trans('app.discount_codes.unused_days_discount', {
                        count: current_subscription.unused_days,
                        days: polishPlurals('dzień', 'dni', 'dni', current_subscription.unused_days),
                        subscription: current_subscription.subscription_type.name
                      })
                    }}
                  </td>
                  <td width="150" class="text-right">- {{ unusedDaysValue | money }}</td>
                </tr>
                <tr v-if="appliedDiscountCode !== null">
                  <td>{{ trans('app.discount_codes.discount_code_discount', { discount_code: appliedDiscountCode.code }) }}</td>
                  <td v-if="appliedDiscountCode.type === 'fixed'" class="text-right">-{{ discountValue | money }}</td>
                  <td v-else class="text-right">
                    -{{ `${appliedDiscountCode.value}% (` }} -{{ discountValue | money }} {{ ')' }}
                  </td>
                </tr>
                <tr>
                  <td class="text-bold text-right">RAZEM DO ZAPŁATY:</td>
                  <td class="text-bold text-right">{{ finalPrice | money }}</td>
                </tr>
                <tr v-if="isFinalPriceNegative">
                  <td colspan="5" class="text-bold text-right">
                    {{ 'app.settings.orders_and_invoices.orders.negative_amount' | trans }}
                  </td>
                </tr>
              </tbody>
            </table>
          </v-flex>
          <v-flex xs12>
            <discount-code-form
              @checkDiscountCode="checkDiscountCode"
              :error="discountCodeError"
              :success-message="discountCodeSuccessMessage"
              :loading="discountCodeButtonLoading"
            />
          </v-flex>
          <v-flex xs12>
            <v-checkbox color="primary" v-model="termsAccepted" name="terms_acceptance">
              <span slot="label">
                Zapoznałem się z <a href="https://dietico.pl/regulamin" target="_blank">Regulaminem</a> serwisu i
                akceptuję jego treść.
              </span>
            </v-checkbox>
          </v-flex>
          <v-flex xs12 v-if="selected_subscription.role_to_assign === DIETITIAN_STUDENT()">
            <v-alert value="true" type="info">
              Aby skorzystać z oferty dla studentów, należy załączyć zdjęcie dwóch stron legitymacji studenckiej lub
              zaświadczenie o byciu studentem. Prosimy o ukrycie wszystkich danych osobowych i pozostawienie jedynie
              imienia i nazwiska oraz daty ważności legitymacji w celu weryfikacji statusu studenta. Po weryfikacji
              wszystkie przesłane pliki zostają automatycznie usunięte.
            </v-alert>
            <input
              class="hidden"
              type="file"
              ref="student_status_verification_files"
              multiple="multiple"
              accept="image/*"
              @change="filesChange"
            />
            <v-btn :disabled="formSubmission" color="primary" @click="filesButtonClick()"
              >{{ 'app.chose_file' | trans }}
            </v-btn>
            <v-label>{{ selectedFilesLabels }}</v-label>
            <v-messages
              class="ml-2"
              v-if="!filesValid"
              :value="[trans('app.student_status_verification_files')]"
              :color="`error`"
            ></v-messages>
          </v-flex>
        </v-layout>
        <v-layout justify-center class="subscription-summary__footer">
          <v-btn :disabled="formSubmission" :href="routes.subscription" color="cancel" dark>Anuluj</v-btn>
          <v-btn :disabled="submitButtonDisabled()" color="primary" @click="placeOrder()"
            >Złóż zamówienie i opłać
          </v-btn>
        </v-layout>
      </v-container>
      <v-dialog v-model="showInvoiceModal" width="800" :persistent="false">
        <order-invoice-form @saved="invoiceSaved">
          <template v-slot:title>
            <v-toolbar card dark color="primary">
              <v-toolbar-title>{{ 'app.invoice.data' | trans }}</v-toolbar-title>
            </v-toolbar>
          </template>
        </order-invoice-form>
      </v-dialog>
    </v-app>
  </base-layout>
</template>

<style lang="scss" type="text/scss">
.subscription-summary__header {
  text-align: center;
}

.subscription-summary__header.header--dates {
  margin-bottom: 35px;
}

.subscription-summary__address {
  white-space: pre-line;
}

.address__header {
  display: block;
  font-weight: bold;
}

.subscription-summary__footer {
  border-top: 1px solid #f4f4f4;
}
</style>

<script>
import { newSubscriptionEndDate, newSubscriptionStartDate } from '../../utils/subscription';
import { polishPlurals } from 'polish-plurals';
import { mapGetters } from 'vuex';
import axios from 'axios';
import { trans } from '../../app';
import {DIETITIAN_PREMIUM, DIETITIAN_STANDARD, DIETITIAN_STUDENT, DIETITIAN_TEST} from "../../utils/roles";

export default {
  props: {
    invoice: Object,
    routes: Object,
    seller: Object,
    last_subscription: Object,
    current_subscription: Object,
    selected_subscription: Object,
  },
  data: () => ({
    appliedDiscountCode: null,
    discountCodeError: null,
    discountCodeSuccessMessage: [],
    discountCodeButtonLoading: false,
    termsAccepted: false,
    formSubmission: false,
    filesValid: true,
    selectedFilesLabels: '',
    unusedDaysValue: 0,
    showInvoiceModal: false,
    displayedInvoice: {},
  }),
  created() {
    if (this.isUpgradingSubscription()) {
      this.unusedDaysValue = this.current_subscription.unused_days_value;
    }
    this.displayedInvoice = {
      name: this.invoice.name,
      street_address: this.invoice.street_address,
      postal_code: this.invoice.postal_code,
      city: this.invoice.city,
      country: this.invoice.country,
      type: this.invoice.type,
      nip: this.invoice.nip,
    };
  },
  methods: {
    DIETITIAN_TEST() {
      return DIETITIAN_TEST
    },
    DIETITIAN_STUDENT() {
      return DIETITIAN_STUDENT
    },
    async checkDiscountCode(e) {
      this.discountCodeButtonLoading = true;
      this.discountCodeError = null;
      this.discountCodeSuccessMessage = [];
      this.appliedDiscountCode = null;
      try {
        const checkDiscountCodeResponse = await axios.post('/v2/discount-codes/check', {
          discount_code: e.discountCode,
          subscription: this.selected_subscription.id,
        });
        this.appliedDiscountCode = checkDiscountCodeResponse.data.data;
        this.discountCodeSuccessMessage = trans('app.discount_codes.code_applied');
      } catch (e) {
        this.discountCodeError = e.response.data.message;
      }
    },
    polishPlurals(singularNominativ, pluralNominativ, pluralGenitive, value) {
      return polishPlurals(singularNominativ, pluralNominativ, pluralGenitive, value);
    },
    placeOrder() {
      let formData = new FormData();

      formData.set('subscription_type_id', this.selected_subscription.id);
      formData.set('terms_acceptance', this.termsAccepted);

      if (typeof this.$refs.student_status_verification_files !== 'undefined') {
        const studentStatusVerificationFiles = this.$refs.student_status_verification_files.files;

        if (this.validateSelectedFiles(studentStatusVerificationFiles) === false) {
          this.filesValid = false;
          return;
        }

        for (let i = 0; i < studentStatusVerificationFiles.length; i++) {
          const file = studentStatusVerificationFiles[i];
          formData.append('student_status_verification_files[' + i + ']', file);
        }
      }

      this.formSubmission = true;

      if (this.appliedDiscountCode) {
        formData.set('discount_code', this.appliedDiscountCode.code);
      }

      axios
        .post(this.routes.subscription_buy, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
        .then((response) => {
          window.location.href = response.data.redirect_route;
        })
        .catch((error) => {
          if (error.response.status && error.response.status === 422) {
            this.$toast.error(error.response.data.message);
          } else {
            this.$toast.error('Nie udało się rozpocząć płatności');
          }
          this.sentryCaptureException(error);
        })
        .finally(() => {
          this.formSubmission = false;
        });
    },
    getGrossPrice() {
      return Number(this.selected_subscription.price);
    },
    getNewSubscriptionStartDate() {
      return newSubscriptionStartDate(
        this.isUpgradingSubscription(),
        this.current_subscription,
        this.last_subscription
      );
    },
    getNewSubscriptionEndDate() {
      return newSubscriptionEndDate(
        this.selectedSubscriptionDuration,
        this.isUpgradingSubscription(),
        this.current_subscription,
        this.last_subscription
      );
    },
    filesButtonClick() {
      this.$refs.student_status_verification_files.click();
    },
    filesChange($event) {
      this.filesValid = true;
      this.selectedFilesLabels = '';
      let files = [];
      Object.values($event.target.files).forEach((file) => {
        files.push(file.name);
      });
      this.selectedFilesLabels = files.join(', ');
    },
    submitButtonDisabled() {
      if (this.formSubmission) {
        return true;
      }

      if (this.termsAccepted === false) {
        return true;
      }

      if (this.selected_subscription.role_to_assign === DIETITIAN_STUDENT) {
        return this.$refs.student_status_verification_files.files.length === 0;
      }

      return false;
    },
    validateSelectedFiles(selectedFiles) {
      if (selectedFiles.length > 2) {
        return false;
      }

      for (let i = 0; i < selectedFiles.length; i++) {
        const selectedFile = selectedFiles[i];

        if (['image/bmp', 'image/jpeg', 'image/png'].includes(selectedFile.type) === false) {
          return false;
        }

        if (selectedFile.size > 2e6) {
          return false;
        }
      }

      return true;
    },
    isUpgradingSubscription() {
      if (this.current_subscription === null) {
        return false;
      }

      if (this.current_subscription.subscription_type.role_to_assign === DIETITIAN_TEST) {
        return false;
      }

      if (this.selected_subscription.id === 30) {
        return false;
      }

      let currentRole = null;
      if (this.current_subscription.subscription_type.id === 30) {
        currentRole = this.current_subscription.order.role_to_assign;
      } else {
        currentRole = this.current_subscription.subscription_type.role_to_assign;
      }

      const subscriptionsRoles = [DIETITIAN_STUDENT, DIETITIAN_STANDARD, DIETITIAN_PREMIUM];

      const newRole = this.selected_subscription.role_to_assign;
      const currentRoleIndex = subscriptionsRoles.indexOf(currentRole);
      const newRoleIndex = subscriptionsRoles.indexOf(newRole);
      return newRoleIndex > currentRoleIndex;
    },
    editInvoice() {
      this.showInvoiceModal = true;
    },
    invoiceSaved(payload) {
      const invoice = payload.invoice;
      this.displayedInvoice = {
        name: invoice.name,
        street_address: invoice.street_address,
        postal_code: invoice.postal_code,
        city: invoice.city,
        country: invoice.country,
        type: invoice.type,
        nip: invoice.nip,
      };
      this.showInvoiceModal = false;
    },
  },
  computed: {
    ...mapGetters(['countryName']),
    discountValue() {
      if (!this.appliedDiscountCode) {
        return 0;
      }
      if (this.appliedDiscountCode.type === 'fixed') {
        return this.appliedDiscountCode.value;
      }

      return Math.ceil((this.getGrossPrice() * this.appliedDiscountCode.value) / 100);
    },
    finalPrice() {
      const result = this.getGrossPrice() - this.unusedDaysValue - this.discountValue;

      return result < 0 ? 0.01 : result;
    },
    isFinalPriceNegative() {
      const result = this.getGrossPrice() - this.unusedDaysValue - this.discountValue;

      return result < 0;
    },
    isDiscountCodeApplied() {
      return this.appliedDiscountCode !== null;
    },
    selectedSubscriptionDuration() {
      if (this.isDiscountCodeApplied) {
          return this.selected_subscription.duration;
      }
      return this.selected_subscription.duration + this.selected_subscription.extra_duration;
    },
  },
};
</script>
